import { Injectable, inject, signal, computed, DestroyRef } from '@angular/core';
import { Observable } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DocumentationApi } from '../api/documentation.api';
import {
  AdditionalDocument,
  EventDocumentation,
  EventAttachment,
  Upload,
} from '../models/documentation.model';
import { BuildingPlan, UploadState } from '../models/building-plan.model';

@Injectable({ providedIn: 'root' })
export class DocumentationStore {
  private documentationApi = inject(DocumentationApi);
  private destroyRef = inject(DestroyRef);

  // ==================== Private Mutable Signals ====================
  private _loading = signal(false);
  private _uploadProgress = signal(0);
  private _uploadState = signal<UploadState>('idle');
  private _buildingPlan = signal<BuildingPlan | null>(null);
  private _additionalDocuments = signal<AdditionalDocument[]>([]);
  private _currentObjectId = signal<number | null>(null);
  private _currentSensorId = signal<number | null>(null);
  private _eventDocumentation = signal<EventDocumentation | null>(null);
  private _eventAttachments = signal<EventAttachment[]>([]);

  // ==================== Public Readonly Signals ====================
  readonly loading = this._loading.asReadonly();
  readonly uploadProgress = this._uploadProgress.asReadonly();
  readonly uploadState = this._uploadState.asReadonly();
  readonly buildingPlan = this._buildingPlan.asReadonly();
  readonly additionalDocuments = this._additionalDocuments.asReadonly();
  readonly currentObjectId = this._currentObjectId.asReadonly();
  readonly currentSensorId = this._currentSensorId.asReadonly();
  readonly eventDocumentation = this._eventDocumentation.asReadonly();
  readonly eventAttachments = this._eventAttachments.asReadonly();

  // ==================== Computed Signals ====================
  readonly hasBuildingPlan = computed(() => !!this._buildingPlan()?.storageLink);
  readonly hasAdditionalDocuments = computed(() => this._additionalDocuments().length > 0);
  readonly additionalDocumentsCount = computed(() => this._additionalDocuments().length);
  readonly isUploading = computed(() => this._uploadState() === 'uploading');
  readonly hasEventDocumentation = computed(() => !!this._eventDocumentation()?.id);
  readonly eventAttachmentsCount = computed(() => this._eventAttachments().length);

  // ==================== Building Plan Operations ====================

  async uploadBuildingPlan(
    objectId: number,
    fileName: string,
    fileData: string
  ): Promise<BuildingPlan> {
    this._uploadState.set('uploading');
    this._uploadProgress.set(0);
    this._currentObjectId.set(objectId);

    try {
      const result = await this.documentationApi.uploadBuildingPlan(
        objectId,
        fileName,
        fileData,
        (progress) => {
          this._uploadProgress.set(progress);
        }
      );

      this._uploadState.set('done');
      this._uploadProgress.set(100);
      this._buildingPlan.set(result);
      return result;
    } catch (error) {
      this._uploadState.set('error');
      this._uploadProgress.set(0);
      throw error;
    }
  }

  async getBuildingPlan(objectId: number): Promise<BuildingPlan | null> {
    this._loading.set(true);
    this._currentObjectId.set(objectId);

    try {
      const result = await this.documentationApi.getBuildingPlan(objectId);
      this._buildingPlan.set(result);
      return result;
    } catch (error) {
      console.error('Failed to get building plan:', error);
      this._buildingPlan.set(null);
      return null;
    } finally {
      this._loading.set(false);
    }
  }

  async deleteBuildingPlan(objectId: number): Promise<boolean> {
    this._loading.set(true);

    try {
      const result = await this.documentationApi.deleteBuildingPlan(objectId);
      if (result) {
        this._buildingPlan.set(null);
      }
      return result;
    } catch (error) {
      console.error('Failed to delete building plan:', error);
      return false;
    } finally {
      this._loading.set(false);
    }
  }

  // ==================== Additional Document Operations ====================

  async uploadAdditionalDocument(
    objectId: number,
    sensorId: number,
    fileName: string,
    fileData: string
  ): Promise<AdditionalDocument> {
    this._uploadState.set('uploading');
    this._uploadProgress.set(0);
    this._currentObjectId.set(objectId);
    this._currentSensorId.set(sensorId);

    try {
      const result = await this.documentationApi.uploadAdditionalDocument(
        objectId,
        sensorId,
        fileName,
        fileData,
        (progress) => {
          this._uploadProgress.set(progress);
        }
      );

      this._uploadState.set('done');
      this._uploadProgress.set(100);

      // Add to local list
      this._additionalDocuments.update((docs) => [...docs, result]);
      return result;
    } catch (error) {
      this._uploadState.set('error');
      this._uploadProgress.set(0);
      throw error;
    }
  }

  async getAdditionalDocuments(sensorId: number): Promise<AdditionalDocument[]> {
    this._loading.set(true);
    this._currentSensorId.set(sensorId);

    try {
      const result = await this.documentationApi.getAdditionalDocuments(sensorId);
      this._additionalDocuments.set(result);
      return result;
    } catch (error) {
      console.error('Failed to get additional documents:', error);
      this._additionalDocuments.set([]);
      return [];
    } finally {
      this._loading.set(false);
    }
  }

  async renameAdditionalDocument(documentId: number, name: string): Promise<AdditionalDocument> {
    this._uploadState.set('uploading');
    this._uploadProgress.set(0);

    try {
      const result = await this.documentationApi.renameAdditionalDocument(
        documentId,
        name,
        (progress) => {
          this._uploadProgress.set(progress);
        }
      );

      this._uploadState.set('done');
      this._uploadProgress.set(100);

      // Update in local list
      this._additionalDocuments.update((docs) =>
        docs.map((doc) => (doc.additionalDocumentId === documentId ? { ...doc, ...result } : doc))
      );
      return result;
    } catch (error) {
      this._uploadState.set('error');
      this._uploadProgress.set(0);
      throw error;
    }
  }

  async deleteAdditionalDocument(documentId: number): Promise<boolean> {
    this._loading.set(true);

    try {
      const result = await this.documentationApi.deleteAdditionalDocument(documentId);
      if (result) {
        this._additionalDocuments.update((docs) =>
          docs.filter((doc) => doc.additionalDocumentId !== documentId)
        );
      }
      return result;
    } catch (error) {
      console.error('Failed to delete additional document:', error);
      return false;
    } finally {
      this._loading.set(false);
    }
  }

  // ==================== State Management ====================

  resetUploadState(): void {
    this._uploadState.set('idle');
    this._uploadProgress.set(0);
  }

  clear(): void {
    this._loading.set(false);
    this._uploadProgress.set(0);
    this._uploadState.set('idle');
    this._buildingPlan.set(null);
    this._additionalDocuments.set([]);
    this._currentObjectId.set(null);
    this._currentSensorId.set(null);
    this._eventDocumentation.set(null);
    this._eventAttachments.set([]);
  }

  // ==================== Event Documentation Operations ====================

  async getUnfinishedDocumentation(
    assetId: number,
    sensorId?: number
  ): Promise<EventDocumentation> {
    this._loading.set(true);

    try {
      const result = await this.documentationApi.getUnfinishedDocumentation(assetId, sensorId);
      this._eventDocumentation.set(result);
      this._currentObjectId.set(assetId);
      if (sensorId) {
        this._currentSensorId.set(sensorId);
      }
      return result;
    } catch (error) {
      console.error('Failed to get unfinished documentation:', error);
      return {} as EventDocumentation;
    } finally {
      this._loading.set(false);
    }
  }

  async finishDocumentation(documentationId: number): Promise<boolean> {
    this._loading.set(true);

    try {
      const result = await this.documentationApi.finishDocumentation(documentationId);
      if (result) {
        this._eventDocumentation.set(null);
        this._eventAttachments.set([]);
      }
      return result;
    } catch (error) {
      console.error('Failed to finish documentation:', error);
      return false;
    } finally {
      this._loading.set(false);
    }
  }

  async updateDocumentation(documentation: EventDocumentation): Promise<EventDocumentation> {
    this._loading.set(true);

    try {
      const result = await this.documentationApi.updateDocumentation(documentation);
      this._eventDocumentation.set(result);
      return result;
    } catch (error) {
      console.error('Failed to update documentation:', error);
      throw error;
    } finally {
      this._loading.set(false);
    }
  }

  async uploadEventAttachment(
    documentationId: number,
    attachmentType: string,
    fileName: string,
    fileData: string
  ): Promise<EventAttachment> {
    this._uploadState.set('uploading');
    this._uploadProgress.set(0);

    try {
      const result = await this.documentationApi.uploadEventAttachment(
        documentationId,
        attachmentType,
        fileName,
        fileData,
        (progress) => {
          this._uploadProgress.set(progress);
        }
      );

      this._uploadState.set('done');
      this._uploadProgress.set(100);

      // Add to local list
      this._eventAttachments.update((attachments) => [...attachments, result]);
      return result;
    } catch (error) {
      this._uploadState.set('error');
      this._uploadProgress.set(0);
      throw error;
    }
  }

  async deleteEventAttachment(attachmentId: number): Promise<boolean> {
    this._loading.set(true);

    try {
      const result = await this.documentationApi.deleteEventAttachment(attachmentId);
      if (result) {
        this._eventAttachments.update((attachments) =>
          attachments.filter((att) => att.id !== attachmentId)
        );
      }
      return result;
    } catch (error) {
      console.error('Failed to delete event attachment:', error);
      return false;
    } finally {
      this._loading.set(false);
    }
  }

  async traceUploadProgress(upload: Observable<any>): Promise<Upload> {
    return new Promise((resolve, reject) => {
      const uploadObservable$ = this.documentationApi.traceUploadProgress(upload);

      uploadObservable$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (uploadState: Upload) => {
          // Update progress signal
          this._uploadProgress.set(uploadState.progress);

          // Update state signal
          if (uploadState.state === 'IN_PROGRESS') {
            this._uploadState.set('uploading');
          } else if (uploadState.state === 'DONE') {
            this._uploadState.set('done');
          } else if (uploadState.state === 'PENDING') {
            this._uploadState.set('uploading');
          }

          // Complete when done
          if (uploadState.state === 'DONE') {
            resolve(uploadState);
          }
        },
        error: (error) => {
          this._uploadState.set('error');
          this._uploadProgress.set(0);
          console.error('Upload progress tracking failed:', error);
          reject(error);
        },
      });
    });
  }
}
